/**
 * Collection: global sold-out ordering (prototype)
 *
 * After the collection grid renders, fetches every other page of the current
 * collection (same filters and sort) through the Section Rendering API, builds
 * the full ordered list (sellable and coming-soon products first, sold-out last,
 * as computed by snippets/product-sold-out-state.liquid, both groups keeping
 * their original relative order) and shows only the slice that belongs to the
 * current page. Non-card children of the grid (banners, promo blocks) keep
 * their position. Re-runs after every facet/pagination update.
 *
 * Config is read from data attributes on <product-list data-global-sold-out-order>.
 */
(() => {
  const GRID_SELECTOR = 'product-list[data-global-sold-out-order]';
  const CARD_SELECTOR = 'product-list > product-card';
  const FETCH_CONCURRENCY = 4;

  let activeController = null;

  const readConfig = (grid) => ({
    sectionId: grid.dataset.sectionId,
    currentPage: Math.max(1, parseInt(grid.dataset.currentPage, 10) || 1),
    totalPages: Math.max(1, parseInt(grid.dataset.totalPages, 10) || 1),
    pageSize: Math.max(1, parseInt(grid.dataset.pageSize, 10) || 24),
    maxPages: Math.max(1, parseInt(grid.dataset.maxPages, 10) || 10)
  });

  const buildPageUrl = (page, sectionId) => {
    const url = new URL(window.location.href);
    url.searchParams.set('page', String(page));
    url.searchParams.set('section_id', sectionId);
    return url.toString();
  };

  const extractCards = (html) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return Array.from(doc.querySelectorAll(CARD_SELECTOR));
  };

  const fetchPageCards = async (page, sectionId, signal) => {
    const response = await fetch(buildPageUrl(page, sectionId), { signal });
    if (!response.ok) {
      throw new Error(`page ${page} responded with ${response.status}`);
    }
    return extractCards(await response.text());
  };

  const fetchAllPages = async (config, currentCards, signal) => {
    const pages = new Array(config.totalPages).fill(null);
    pages[config.currentPage - 1] = currentCards;

    const pending = [];
    for (let page = 1; page <= config.totalPages; page += 1) {
      if (page !== config.currentPage) pending.push(page);
    }

    const worker = async () => {
      while (pending.length > 0) {
        const page = pending.shift();
        pages[page - 1] = await fetchPageCards(page, config.sectionId, signal);
      }
    };

    await Promise.all(Array.from({ length: FETCH_CONCURRENCY }, worker));
    return pages.flat();
  };

  const isSoldOut = (card) => card.dataset.soldOut === 'true';

  const partitionBySoldOut = (cards) => [...cards.filter((card) => !isSoldOut(card)), ...cards.filter(isSoldOut)];

  const collectNonCardChildren = (grid) =>
    Array.from(grid.children)
      .map((node, index) => ({ node, index }))
      .filter(({ node }) => node.tagName.toLowerCase() !== 'product-card');

  const renderSlice = (grid, orderedCards, keptChildren, config) => {
    const start = (config.currentPage - 1) * config.pageSize;
    const nodes = orderedCards
      .slice(start, start + config.pageSize)
      .map((card) => document.importNode(card, true));

    keptChildren.forEach(({ node, index }) => {
      nodes.splice(Math.min(index, nodes.length), 0, node);
    });

    nodes.forEach((node) => {
      node.style.opacity = '1';
      node.style.transform = 'none';
    });

    grid.replaceChildren(...nodes);
  };

  const applyGlobalOrder = async (grid) => {
    const config = readConfig(grid);

    if (!config.sectionId) return;

    if (config.totalPages > config.maxPages) {
      grid.dataset.globalOrderState = 'skipped';
      return;
    }

    if (activeController) activeController.abort();
    activeController = new AbortController();
    const { signal } = activeController;

    const currentCards = Array.from(grid.querySelectorAll(':scope > product-card'));
    const keptChildren = collectNonCardChildren(grid);

    grid.dataset.globalOrderState = 'loading';

    try {
      const allCards = await fetchAllPages(config, currentCards, signal);
      if (signal.aborted) return;

      const ordered = partitionBySoldOut(allCards);
      renderSlice(grid, ordered, keptChildren, config);

      grid.dataset.globalOrderState = 'done';
    } catch (_error) {
      if (signal.aborted) return;
      grid.dataset.globalOrderState = 'error';
    }
  };

  const run = () => {
    document.querySelectorAll(GRID_SELECTOR).forEach((grid) => {
      applyGlobalOrder(grid);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  document.addEventListener('facet:updated', run);
})();
